import React from 'react';
import { SpendingSumModel } from 'src/types/spending-sum.model';
import styles from './spendingSumGrid.module.scss';
import { SpendingSumCollapsible } from 'src/components/atomic/organisms/spendingSumCollapsible/SpendingSumCollapsible';
import DateUtils from 'src/utils/date';
import { PageTimePeriod } from 'src/types/page-time-period.enum';
import { useSpendingSums } from 'src/hooks/queries/spending';
import { useCategories } from 'src/hooks/queries/categories';
import CategoryService from 'src/services/category.service';
import Loading from '../../atoms/loading/Loading';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import colors from 'src/styles/colors.module.scss';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import EmptyChartState from '../../atoms/emptyChartState/EmptyChartState';
import { IconCardSwipe } from '@americanexpress/dls-icons';
import LiteralsService from 'src/services/literals/LiteralsService';
import { useSyncingAccounts } from 'src/hooks/useSyncingAccounts';
import TotalAverageInfoModal from 'src/components/atomic/organisms/totalAverageInfoModal/TotalAverageInfoModal';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import { WalkmeName, WalkmePopupWrapper } from 'src/components/atomic/organisms/walkme';

// helper to split array in half
const splitArray = (a: SpendingSumModel[][]): [SpendingSumModel[][], SpendingSumModel[][]] => {
    const splitIndex = a.length < 12 ? 6 : Math.ceil(a.length / 2);
    return [a.slice(0, splitIndex), a.slice(splitIndex)];
};

const getChildrenSum = (a: SpendingSumModel[][]): number => {
    let sum = 0;
    a.forEach((e) => {
        for (let i = 1; i < e.length; i++) sum += e[i].spent;
    });

    return sum;
};

export default function SpendingSumGrid({ timePeriod }: { timePeriod: PageTimePeriod }): JSX.Element {
    const { isMinWidthMedium } = useBreakpoints();
    const { isLoading: isLoadingCategories, data: categories } = useCategories();
    const { isLoading: isLoadingSpendingSums, data: spendingSums } = useSpendingSums(
        DateUtils.getDatesFromTimePeriod(timePeriod)
    );
    const { isSyncingAccounts } = useSyncingAccounts();

    const header: JSX.Element =
        timePeriod === PageTimePeriod.CURRENT_MONTH ? (
            <div className={styles.currentMonthHeader}>
                <WalkmePopupWrapper walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION} stepIndex={2}>
                    <Typography
                        component="h2"
                        variant={TypographyVariants.HEADING_S}
                        className={colors.brandStrong}
                        data-cy={Selectors.SpendSmart.SPENDING_SUM_GRID_CURRENT_MONTH_HEADER}
                    >
                        {LiteralsService.getLiteral('SpendingCategories.spendingByCategory')}
                    </Typography>
                </WalkmePopupWrapper>
                <TotalAverageInfoModal
                    anchorTitle={LiteralsService.getLiteral('TotalAverageInfoModal.anchorTitle2')}
                    anchorTag={adobeTags.TotalAverageInfoModal.OpenTotalAverage}
                />
            </div>
        ) : (
            <div className={styles.historyHeader}>
                <Typography
                    variant={TypographyVariants.HEADING_S}
                    className={colors.brandStrong}
                    component="h2"
                    data-cy={Selectors.SpendSmart.SPENDING_SUM_GRID_HISTORY_HEADER}
                >
                    {LiteralsService.getLiteral('SpendingCategories.totalYearlySpending')}
                </Typography>
                <Typography variant={TypographyVariants.SUBTEXT} className={colors.textWeak}>
                    {LiteralsService.getLiteral('SpendingCategories.basedOn')}
                </Typography>
            </div>
        );

    if (isLoadingCategories || isLoadingSpendingSums || isSyncingAccounts)
        return (
            <div className={styles.container}>
                {header}
                <Loading lg />
            </div>
        );

    const familySpendingSums = CategoryService.groupByCategory(
        spendingSums,
        categories,
        (spendingSum) => -spendingSum.spent
    )
        // filter out parent categories with no spending
        .filter((e) => e[0].spent)
        // filter out all children with no spending
        .map((e) => e.filter(({ spent }) => spent)) as SpendingSumModel[][];

    const noChildrenInGrid: boolean = !getChildrenSum(familySpendingSums);
    const [leftFamilySpendingSums, rightFamilySpendingSums] = splitArray(familySpendingSums);

    return (
        <div className={styles.container}>
            {header}
            {!familySpendingSums || familySpendingSums.length === 0 ? (
                <EmptyChartState
                    icon={<IconCardSwipe size="lg" />}
                    text={LiteralsService.getLiteral('General.grid.noDataText')}
                    subtext={LiteralsService.getLiteral('General.grid.noDataSubtext')}
                    data-cy={Selectors.SpendSmart.EMPTY_CHART}
                />
            ) : (
                <div className={styles.gridContainer}>
                    <div className={styles.leftColumn}>
                        {leftFamilySpendingSums.map((e, i) => (
                            <SpendingSumCollapsible
                                key={`SpendingSumCollapsible-left-${i}`}
                                spendingSumParent={e[0]}
                                spendingSumChildren={e.slice(1)}
                                categories={categories}
                                noChildrenInGrid={noChildrenInGrid}
                                topBorder={i !== 0}
                            />
                        ))}
                    </div>
                    <div className={styles.rightColumn}>
                        {rightFamilySpendingSums.map((e, i) => (
                            <SpendingSumCollapsible
                                key={`SpendingSumCollapsible-right-${i}`}
                                spendingSumParent={e[0]}
                                spendingSumChildren={e.slice(1)}
                                categories={categories}
                                noChildrenInGrid={noChildrenInGrid}
                                topBorder={!isMinWidthMedium || i !== 0}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
